import { srcSetFor, baseOf, SIZES } from "../lib/img";
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import LocaleLink from "./LocaleLink";
import { useL, useLocale } from "../i18n/LocaleContext";
import "./ScrollStory.css";

/* ScrollStory — pinned/sticky scroll-storytelling section
   ────────────────────────────────────────────────────────────────────
   Owner spec (round 50): the section occupies the full viewport and
   stays visually PINNED while the visitor scrolls. Scroll gestures
   inside the section progress through the steps; both the pinned
   image AND the copy panel crossfade to the active step. After the
   last step, the section releases and the page continues.

   Implementation: an outer container tall enough for N * ~100vh of
   scroll distance wraps a sticky inner stage that is exactly 100vh
   tall. The active step index is computed from the outer
   container's scroll progress on a rAF-throttled scroll listener.

   Props:
     - steps: [{ number?, eyebrow?, title, body, chips?, image, imageAlt, cta? }]
             number defaults to the 1-indexed position, formatted as "01".
             cta = { label, href }.
     - kicker / title: optional header content, rendered above the first step.
     - headingLevel: 2 (default) or 1. The /programs page has no other
             page heading, so it renders the story title as its <h1>;
             the home page keeps it as an <h2> under the hero. The CSS
             class is the same either way, so the look does not change.
     - mediaSide: "left" (default per round 50 spec) | "right".
     - variant: "dark" (default) | "light" (warm-panel surface).

   TRILINGUAL: every copy prop may be either a plain string or a
   { en, ar, ru } triple. The component resolves `steps` with useL and
   the header props with t(), and `resolve` passes plain strings
   through untouched — so all four callers (Home, Programs, UK2026,
   DubaiWinter) work whether or not their data has been converted yet.

   RTL: the stage is a 2-column grid driven by grid-template-areas, so
   mediaSide mirrors itself under dir="rtl" (media="left" lands on the
   right in Arabic, which is the correct mirror). The scroll maths is
   purely vertical (rect.top / rect.height), so there is no translateX
   to correct and no moving track that needs direction: ltr.

   Never repeat the same image inside a single story — the caller
   is responsible for merging steps to match distinct images. */

const ARROW = (
  <svg className="i-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);

/* Component-owned copy. */
const SECTION_LABEL = {
  en: "Scrolling story",
  ar: "قصة متسلسلة",
  ru: "История с прокруткой",
};
/* Reads "01 of 04". The surrounding spaces are part of the string in
   all three languages. */
const COUNTER_SEP = { en: " of ", ar: " من ", ru: " из " };

function pad(n) { return String(n).padStart(2, "0"); }

/* Local JPG → the WebP srcset written by scripts/image-variants.mjs
   (three sizes). Steps can override with `imageWebp` (one file). */
function webpFor(src, override) {
  if (override) return override;
  if (typeof src !== "string") return null;
  return srcSetFor(baseOf(src));
}

/* Both hooks start from a fixed desktop value and read the real one in
   a layout effect, before the first paint. The page HTML is pre-rendered
   at build time on a desktop viewport, and React can only hydrate that
   HTML if the first client render produces the same tree. The correct
   layout is committed synchronously right after, so nothing flashes. */
function useReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useLayoutEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(mq.matches);
    const handler = (e) => setReduced(e.matches);
    mq.addEventListener?.("change", handler);
    return () => mq.removeEventListener?.("change", handler);
  }, []);
  return reduced;
}

function useViewportWidth() {
  const [w, setW] = useState(1024);
  useLayoutEffect(() => {
    if (typeof window === "undefined") return;
    setW(window.innerWidth);
    const onResize = () => setW(window.innerWidth);
    window.addEventListener("resize", onResize, { passive: true });
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return w;
}

/* href is an in-site path when it starts with "/", so it goes through
   LocaleLink and keeps the visitor in their language. Hash-only and
   external hrefs are left exactly as they are.
   `tabIndex` is -1 on the steps of the pinned story that are not on
   screen (they are aria-hidden), so Tab only reaches the visible one. */
function TitleLink({ href, children, className, tabIndex }) {
  if (!href) return <span className={className}>{children}</span>;
  const isRoute = typeof href === "string" && href.startsWith("/");
  return isRoute
    ? <LocaleLink to={href} className={className} tabIndex={tabIndex}>{children}</LocaleLink>
    : <a href={href} className={className} tabIndex={tabIndex}>{children}</a>;
}

function CTA({ cta, name, tabIndex }) {
  if (!cta) return null;
  const isRoute = typeof cta.href === "string" && cta.href.startsWith("/");
  /* "Learn more" alone says nothing to a screen reader or a crawler, so
     the step's name follows it, visually hidden. */
  const label = <>{cta.label}{name && <span className="visually-hidden">: {name}</span>} {ARROW}</>;
  return isRoute
    ? <LocaleLink to={cta.href} className="ss-cta" tabIndex={tabIndex}>{label}</LocaleLink>
    : <a
        href={cta.href}
        className="ss-cta"
        tabIndex={tabIndex}
        target={cta.external ? "_blank" : undefined}
        rel={cta.external ? "noopener noreferrer" : undefined}
      >{label}</a>;
}

export default function ScrollStory({
  steps,
  kicker,
  title,
  mediaSide = "left",
  variant = "dark",
  id,
  headingLevel = 2,
}) {
  const { t } = useLocale();
  const TitleTag = headingLevel === 1 ? "h1" : "h2";
  /* Resolve the whole step list for the active language in one call.
     Plain strings pass through, so untranslated callers keep working. */
  const localizedSteps = useL(steps);
  const kickerText = t(kicker);
  const titleText = t(title);
  const counterSep = t(COUNTER_SEP);
  const sectionLabel = titleText || t(SECTION_LABEL);

  const normalized = useMemo(
    () => (localizedSteps || []).map((s, i) => ({ ...s, number: s.number || pad(i + 1) })),
    [localizedSteps]
  );
  const total = normalized.length;

  const [activeIdx, setActiveIdx] = useState(0);
  const activeIdxRef = useRef(0);

  const outerRef = useRef(null);
  const reduced = useReducedMotion();
  const vw = useViewportWidth();
  const isMobile = vw < 900;
  const pinned = !reduced && !isMobile && total > 0;

  /* Preload + decode every step image on mount so no decode-during-
     scroll ever happens. HTMLImageElement.decode() lands off the
     main thread on modern browsers. The same srcset and sizes as the
     <picture> below, so the browser warms the file it will draw, not
     the full JPEG (that doubled every photo's download, 2026-10-08). */
  useEffect(() => {
    if (typeof window === "undefined" || total === 0) return;
    normalized.forEach((s) => {
      if (!s.image) return;
      const img = new Image();
      img.decoding = "async";
      const webp = webpFor(s.image, s.imageWebp);
      if (webp && webp.includes(" ")) {
        img.sizes = pinned ? SIZES.half : SIZES.full;
        img.srcset = webp;
      } else {
        img.src = webp || s.image;
      }
      if (typeof img.decode === "function") {
        img.decode().catch(() => {});
      }
    });
  }, [normalized, total, pinned]);

  /* Scroll-driven active-step calculation for the pinned mode.
     rAF-throttled. State updates only when active index changes.
     - progress = clamp((-rect.top) / (rect.height - vh), 0, 1)
     - activeIdx = clamp(floor(progress * total), 0, total - 1)

     Vertical only, so it is identical in LTR and RTL.
   */
  useEffect(() => {
    if (!pinned) {
      /* Non-pinned (mobile / reduced motion): show all steps in
         normal document flow; active-step decoration follows the
         viewport middle so the counter still updates as the user
         scrolls the stacked layout. */
      setActiveIdx(0);
      activeIdxRef.current = 0;
      return;
    }
    let raf = 0;
    let ticking = false;

    function compute() {
      ticking = false;
      const el = outerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight || 1;
      const scrollable = rect.height - vh;
      if (scrollable <= 0) return;
      const rawProgress = (-rect.top) / scrollable;
      const p = Math.min(1, Math.max(0, rawProgress));
      /* Split the [0, 1] range into N equal slots. Each slot
         corresponds to one step. Using `Math.min(total-1, ...)` so
         the very last position (p === 1) still resolves to the last
         index rather than out-of-bounds. */
      const idx = Math.min(total - 1, Math.max(0, Math.floor(p * total)));
      if (idx !== activeIdxRef.current) {
        activeIdxRef.current = idx;
        setActiveIdx(idx);
      }
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      raf = requestAnimationFrame(compute);
    }

    /* Initial compute in case the section is already in view on
       mount (e.g. hash navigation directly to #programs). */
    compute();

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [pinned, total]);

  /* ── Pinned (desktop) layout ── */
  if (pinned) {
    const outerHeight = `calc(${total} * 100svh)`;

    return (
      <section
        ref={outerRef}
        id={id}
        className={`ss-pin ss-media-${mediaSide}${variant === "light" ? " ss-pin--light" : ""}`}
        style={{ height: outerHeight }}
        aria-label={sectionLabel}
      >
        <div className="ss-pin-stage">
          {/* Media panel */}
          <div className="ss-pin-media" aria-hidden="true">
            {normalized.map((s, i) => {
              const webp = webpFor(s.image, s.imageWebp);
              return (
                <picture
                  key={i}
                  className={`ss-photo-slot${i === activeIdx ? " is-active" : ""}`}
                >
                  {webp && <source srcSet={webp} sizes={SIZES.half} type="image/webp" />}
                  <img
                    className="ss-photo"
                    src={s.image}
                    alt=""
                    loading="eager"
                    fetchPriority={i === 0 ? "high" : "low"}
                    draggable="false"
                  />
                </picture>
              );
            })}
          </div>

          {/* Copy panel */}
          <div className="ss-pin-copy">
            {(kickerText || titleText) && (
              <header className="ss-pin-head">
                {kickerText && <span className="ss-kicker">{kickerText}</span>}
                {titleText && <TitleTag className="ss-pin-title pg-display">{titleText}</TitleTag>}
              </header>
            )}

            <div className="ss-pin-steps">
              {normalized.map((s, i) => (
                <article
                  key={i}
                  className={`ss-pin-step${i === activeIdx ? " is-active" : ""}`}
                  aria-hidden={i !== activeIdx}
                >
                  <p className="ss-counter">
                    <span className="ss-counter-num">{s.number}</span>
                    <span className="ss-counter-sep">{counterSep}</span>
                    <span className="ss-counter-total">{pad(total)}</span>
                  </p>
                  {s.eyebrow && <p className="ss-eyebrow">{s.eyebrow}</p>}
                  <h3 className="ss-step-title pg-display">
                    <TitleLink href={s.cta?.href} className="ss-step-title-link" tabIndex={i === activeIdx ? undefined : -1}>
                      {s.title}
                    </TitleLink>
                  </h3>
                  <p className="ss-step-body-copy">{s.body}</p>
                  {s.chips && s.chips.length > 0 && (
                    <ul className="ss-chips" role="list">
                      {s.chips.map((c, ci) => (
                        <li key={ci} className="ss-chip">{c}</li>
                      ))}
                    </ul>
                  )}
                  {s.cta && <CTA cta={s.cta} name={s.title} tabIndex={i === activeIdx ? undefined : -1} />}
                </article>
              ))}
            </div>

            {/* Progress rail: N vertical ticks, active tick filled */}
            <div className="ss-pin-rail" aria-hidden="true">
              {normalized.map((_, i) => (
                <span key={i} className={`ss-pin-rail-tick${i === activeIdx ? " is-active" : ""}`} />
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  /* ── Stacked layout (mobile, reduced motion) ──
     Each step is its own block with its own image above the copy.
     No pinning, no sticky, no crossfade. */
  return (
    <section
      className={`ss-stack${variant === "light" ? " ss-stack--light" : ""}`}
      aria-label={sectionLabel}
    >
      {(kickerText || titleText) && (
        <header className="ss-stack-head">
          {kickerText && <span className="ss-kicker">{kickerText}</span>}
          {titleText && <TitleTag className="ss-pin-title pg-display">{titleText}</TitleTag>}
        </header>
      )}
      <ol className="ss-stack-steps" role="list">
        {normalized.map((s, i) => {
          const webp = webpFor(s.image, s.imageWebp);
          return (
            <li
              key={i}
              className="ss-stack-step"
              id={i === 0 ? id : undefined}
            >
              <picture className="ss-stack-media">
                {webp && <source srcSet={webp} sizes={SIZES.full} type="image/webp" />}
                <img
                  src={s.image}
                  alt={s.imageAlt || s.title}
                  loading={i === 0 ? "eager" : "lazy"}
                />
              </picture>
              <div className="ss-stack-body">
                <p className="ss-counter">
                  <span className="ss-counter-num">{s.number}</span>
                  <span className="ss-counter-sep">{counterSep}</span>
                  <span className="ss-counter-total">{pad(total)}</span>
                </p>
                {s.eyebrow && <p className="ss-eyebrow">{s.eyebrow}</p>}
                <h3 className="ss-step-title pg-display">
                  <TitleLink href={s.cta?.href} className="ss-step-title-link">
                    {s.title}
                  </TitleLink>
                </h3>
                <p className="ss-step-body-copy">{s.body}</p>
                {s.chips && s.chips.length > 0 && (
                  <ul className="ss-chips" role="list">
                    {s.chips.map((c, ci) => (
                      <li key={ci} className="ss-chip">{c}</li>
                    ))}
                  </ul>
                )}
                {s.cta && <CTA cta={s.cta} name={s.title} />}
              </div>
            </li>
          );
        })}
      </ol>
    </section>
  );
}
